<template>
  <div class="delivery-note-index-page po main-content">
    <div class="page-header">
      <h3 class="page-title">{{ $t("deliveryNotes.title") }}</h3>
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="dashboard">
            {{ $t("general.home") }}
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="#">
            {{ $tc("deliveryNotes.delivery_note", 2) }}
          </router-link>
        </li>
      </ol>
    </div>

    <transition name="fade">
      <div v-show="showFilters" class="filter-section">
        <div class="filter-container">
          <div class="filter-customer">
            <label>{{ $tc("customers.customer", 1) }} </label>
            <base-select
              v-model="filters.customer"
              :options="customers"
              :searchable="true"
              :show-labels="false"
              :allow-empty="true"
              :tabindex="8"
              :placeholder="$t('customers.type_or_click')"
              label="name"
              track-by="id"
            />
          </div>
          <div class="filter-status">
            <label>{{ $t("deliveryNotes.status") }}</label>
            <base-select
              v-model="filters.status"
              :options="status"
              :searchable="true"
              :show-labels="false"
              :placeholder="$t('general.select_a_status')"
              @remove="clearStatusSearch()"
            />
          </div>
          <div class="filter-date">
            <div class="from pr-3">
              <label>{{ $t("general.from") }}</label>
              <base-date-picker
                v-model="filters.from_date"
                :calendar-button="true"
                calendar-button-icon="calendar"
              />
            </div>
            <div class="dashed" />
            <div class="to pl-3">
              <label>{{ $t("general.to") }}</label>
              <base-date-picker
                v-model="filters.to_date"
                :calendar-button="true"
                calendar-button-icon="calendar"
              />
            </div>
          </div>
          <div class="filter-po">
            <label>{{ $t("deliveryNotes.delivery_note_number") }}</label>
            <base-input v-model="filters.delivery_note_number" icon="hashtag" />
          </div>
        </div>
        <label class="clear-filter" @click="clearFilter">{{
          $t("general.clear_all")
        }}</label>
      </div>
    </transition>
    <div
      v-cloak
      v-show="showEmptyScreen"
      class="col-xs-1 no-data-info"
      align="center"
    >
      <moon-walker-icon class="mt-5 mb-4" />
      <div class="row" align="center">
        <label class="col title">{{
          $t("deliveryNotes.no_delivery_note")
        }}</label>
      </div>
      <div class="row">
        <label class="description col mt-1" align="center">{{
          $t("deliveryNotes.list_of_delivery_note")
        }}</label>
      </div>
    </div>

    <div v-show="!showEmptyScreen" class="table-container">
      <div class="table-actions mt-5">
        <p class="table-stats">
          {{ $t("general.showing") }}: <b>{{ deliveryNotes.length }}</b>
          {{ $t("general.of") }} <b>{{ totalDeliveryNotes }}</b>
        </p>

        <!-- Tabs -->
        <ul class="tabs">
          <li class="tab" @click="getStatus('')">
            <a
              :class="[
                'tab-link',
                {
                  'a-active':
                    filters.status === '' ||
                    (filters.status !== 'DRAFT' &&
                      filters.status !== 'SENT' &&
                      filters.status !== 'ACCEPTED' &&
                      filters.status !== 'REJECTED' &&
                      filters.status !== 'SETTLED')
                }
              ]"
              href="#"
              >{{ $t("general.all") }}</a
            >
          </li>
        </ul>
        <transition name="fade">
          <v-dropdown v-if="selectedDeliveryNotes.length" :show-arrow="false">
            <span
              slot="activator"
              href="#"
              class="table-actions-button dropdown-toggle"
            >
              {{ $t("general.actions") }}
            </span>
            <v-dropdown-item>
              <div class="dropdown-item" @click="removeMultipleDeliveryNotes">
                <font-awesome-icon
                  :icon="['fas', 'trash']"
                  class="dropdown-item-icon"
                />
                {{ $t("general.delete") }}
              </div>
            </v-dropdown-item>
          </v-dropdown>
        </transition>
      </div>

      <div class="custom-control custom-checkbox">
        <input
          id="select-all"
          v-model="selectAllFieldStatus"
          :disabled="deliveryNotes.length <= 0"
          type="checkbox"
          class="custom-control-input"
          @change="selectAllDeliveryNotes"
        />
        <label
          v-show="!isRequestOngoing"
          for="select-all"
          class="custom-control-label selectall"
        >
          <span class="select-all-label">{{ $t("general.select_all") }} </span>
        </label>
      </div>

      <table-component
        ref="table"
        :show-filter="false"
        :data="fetchData"
        table-class="table"
      >
        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="no-click"
        >
          <template slot-scope="row">
            <div class="custom-control custom-checkbox">
              <input
                :id="row.id"
                v-model="selectField"
                :value="row.id"
                type="checkbox"
                class="custom-control-input"
              />
              <label :for="row.id" class="custom-control-label" />
            </div>
          </template>
        </table-column>
        <table-column
          :label="$t('deliveryNotes.date')"
          sort-as="delivery_note_date"
          show="formattedDeliveryNoteDate"
        />
        <!-- <table-column
          :label="$tc('deliveryNotes.delivery_note', 1)"
          show="delivery_note_number"
          sort-as="delivery_note_number"
        /> -->
        <!-- <table-column
          :label="$tc('pos.purchase_order_number')"
          show="purchase_order.purchase_order_number"
          sort-as="purchase_order_number"
        /> -->
        <table-column
          :label="$tc('pos.po_num')"
          show="purchase_order.reference_number"
        />
        <!-- <table-column
          :label="$tc('pos.total')"
          show="total"/> -->
        <table-column :label="$t('deliveryNotes.customer')">
          <template slot-scope="row">
            <p v-if="row.buyer_stakeholder">
              <span
                v-if="
                  row.buyer_stakeholder.details.company &&
                    row.buyer_stakeholder.details.company.name
                "
              >
                {{ row.buyer_stakeholder.details.company.name }}
              </span>
              <!-- {{ row.buyer_stakeholder.details.user.name }} -->
            </p>
          </template>
        </table-column>
        <table-column :label="$tc('deliveryNotes.status')" show="status">
          <template slot-scope="row">
            <span> {{ $t("deliveryNotes.status") }}</span>
            <span :class="'deliveryNote-status-' + row.status.toLowerCase()">{{
              row.status
            }}</span>
          </template>
        </table-column>

        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="action-dropdown"
        >
          <template slot-scope="row">
            <span> {{ $t("deliveryNotes.action") }} </span>
            <v-dropdown>
              <a slot="activator" href="#">
                <dot-icon />
              </a>
              <v-dropdown-item>
                <router-link
                  :to="{ path: `delivery-notes/${row.id}/view` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon icon="eye" class="dropdown-item-icon" />
                  {{ $t("general.view") }}
                </router-link>
              </v-dropdown-item>
              <v-dropdown-item>
                <div
                  class="dropdown-item"
                  @click="downloadDeliveryNote(row.unique_hash)"
                >
                  <font-awesome-icon
                    icon="download"
                    class="dropdown-item-icon"
                  />
                  {{ $t("general.doawnload_excel") }}
                </div>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="
                  row.status == 'DRAFT' &&
                    row.purchase_order.items &&
                    row.purchase_order.items.length > 0
                "
              >
                <router-link
                  :to="{ path: `delivery-notes/${row.id}/edit` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon
                    :icon="['fas', 'pencil-alt']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("general.edit") }}
                </router-link>
              </v-dropdown-item>
              <v-dropdown-item v-else-if="row.status == 'DRAFT'">
                <router-link
                  :to="{ path: `manually-delivery-notes/${row.id}/edit` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon
                    :icon="['fas', 'pencil-alt']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("general.edit") }}
                </router-link>
              </v-dropdown-item>
              <v-dropdown-item v-if="row.status == 'DRAFT'">
                <div class="dropdown-item" @click="removeDeliveryNote(row.id)">
                  <font-awesome-icon
                    :icon="['fas', 'trash']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("general.delete") }}
                </div>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="row.status == 'DRAFT' && row.items.length > 0"
              >
                <div class="dropdown-item" @click="stableDeliveryNote(row.id)">
                  <font-awesome-icon
                    icon="check-circle"
                    class="dropdown-item-icon"
                  />
                  {{ $t("deliveryNotes.mark_as_cofirmed") }}
                </div>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="
                  row.status == 'CONFIRMED' || row.status == 'PARTIALLY_SETTLED'
                "
              >
                <a
                  class="dropdown-item"
                  href="#/"
                  @click="convertInToinvoice(row.id)"
                >
                  <font-awesome-icon
                    icon="file-alt"
                    class="dropdown-item-icon"
                  />
                  {{ $t("pos.convert_to_invoice") }}
                </a>
              </v-dropdown-item>
            </v-dropdown>
          </template>
        </table-column>
      </table-component>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import MoonWalkerIcon from "../../../js/components/icon/MoonwalkerIcon";
import { SweetModal, SweetModalTab } from "sweet-modal-vue";
import ObservatoryIcon from "../../components/icon/ObservatoryIcon";
import moment from "moment";

export default {
  components: {
    "moon-walker-icon": MoonWalkerIcon
  },
  data() {
    return {
      showFilters: false,
      currency: null,
      status: ["DRAFT"],
      filtersApplied: false,
      isRequestOngoing: true,
      filters: {
        customer: "",
        status: "",
        from_date: "",
        to_date: "",
        delivery_note_number: ""
      },
      userType: null
    };
  },

  computed: {
    focus,
    showEmptyScreen() {
      return (
        !this.totalDeliveryNotes &&
        !this.isRequestOngoing &&
        !this.filtersApplied
      );
    },
    filterIcon() {
      return this.showFilters ? "times" : "filter";
    },
    ...mapGetters("customer", ["customers"]),
    ...mapGetters("currency", ["defaultCurrency", "currencies"]),
    ...mapGetters("deliveryNote", [
      "selectedDeliveryNotes",
      "totalDeliveryNotes",
      "deliveryNotes",
      "selectAllField"
    ]),
    ...mapGetters("po", ["POForDeliveryNotes"]),
    selectField: {
      get: function() {
        return this.selectedDeliveryNotes;
      },
      set: function(val) {
        this.selectdDeliveryNote(val);
      }
    },
    selectAllFieldStatus: {
      get: function() {
        return this.selectAllField;
      },
      set: function(val) {
        this.setSelectAllState(val);
      }
    }
  },
  watch: {
    filters: {
      handler: "setFilters",
      deep: true
    }
  },
  created() {
    if (localStorage.getItem("role") === "super_admin") {
      this.userType = "super_admin";
    } else if (localStorage.getItem("account_type") === "anchor") {
      this.userType = "anchor";
    } else if (localStorage.getItem("account_type") === "funder") {
      this.userType = "funder";
    } else if (localStorage.getItem("account_type") === "supplier") {
      this.userType = "supplier";
    }
    if (this.userType === "supplier") {
      this.fetchCustomers();
    }
  },
  destroyed() {
    if (this.selectAllField) {
      this.selectAllDeliveryNotes();
    }
    if (this.POForDeliveryNotes) {
      this.setPOForDeliveryNotes([]);
    }
  },
  mounted() {},
  methods: {
    ...mapActions("po", ["setPOForDeliveryNotes", "setPOForInvoices"]),
    ...mapActions("deliveryNote", [
      "fetchDeliveryNotes",
      "resetSelectedDeliveryNotes",
      "selectdDeliveryNote",
      "deleteDeliveryNote",
      "deleteMultiplePOs",
      "setSelectAllState",
      "selectAllDeliveryNotes",
      "markAsStable",
      "convertToInvoice"
    ]),
    ...mapActions("customer", ["fetchCustomers"]),
    refreshTable() {
      this.$refs.table.refresh();
    },
    getStatus(val) {
      this.filters.status = val;
    },
    async fetchData({ page, per_page, filter, sort }) {
      let Data = {
        customer_id:
          this.filters.customer === ""
            ? this.filters.customer
            : this.filters.customer.id,
        status: this.filters.status,
        from_date:
          this.filters.from_date === ""
            ? this.filters.from_date
            : moment(this.filters.from_date).format("DD/MM/YYYY"),
        to_date:
          this.filters.to_date === ""
            ? this.filters.to_date
            : moment(this.filters.to_date).format("DD/MM/YYYY"),
        delivery_note_number: this.filters.delivery_note_number,
        purchase_order_id: this.POForDeliveryNotes,
        orderByField: sort.fieldName || "created_at",
        orderBy: sort.order || "desc",
        per_page, // Add rowsPerPage in the API request
        page
      };
      this.isRequestOngoing = true;
      let response = await this.fetchDeliveryNotes(Data);

      this.isRequestOngoing = false;

      this.currency = response.data.currency;

      return {
        data: response.data.deliveryNotes.data,
        pagination: {
          totalPages: response.data.deliveryNotes.last_page,
          currentPage: page,
          count: response.data.deliveryNotes.count,
          rowsPerPage: response.data.deliveryNotes.per_page // Add rowsPerPage in the API request
        }
      };
    },
    setFilters() {
      this.filtersApplied = true;
      this.resetSelectedDeliveryNotes();
      this.$refs.table.refresh();
    },
    clearFilter() {
      this.filters = {
        customer: "",
        status: "",
        from_date: "",
        to_date: "",
        delivery_note_number: ""
      };

      this.$nextTick(() => {
        this.filtersApplied = false;
      });
    },
    toggleFilter() {
      if (this.showFilters && this.filtersApplied) {
        this.clearFilter();
        this.refreshTable();
      }

      this.showFilters = !this.showFilters;
    },
    onSelectCustomer(customer) {
      this.filters.customer = customer;
    },
    async clearCustomerSearch(removedOption, id) {
      this.filters.customer = "";
      this.refreshTable();
    },
    async clearStatusSearch(removedOption, id) {
      this.filters.status = "";
      this.refreshTable();
    },
    async removeMultipleDeliveryNotes() {},
    async removeDeliveryNote(id) {
      this.id = id;
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$tc("deliveryNotes.confirm_delete"),
        icon: "/assets/icon/trash-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async value => {
        if (value) {
          let res = await this.deleteDeliveryNote(this.id);
          if (res.data.success) {
            window.toastr["success"](this.$tc("deliveryNotes.deleted_message"));
            this.$refs.table.refresh();
            return true;
          }

          if (res.data.error === "payment_attached") {
            window.toastr["error"](
              this.$t("invoices.payment_attached_message"),
              this.$t("general.action_failed")
            );
            return true;
          }

          window.toastr["error"](res.data.error);
          return true;
        }

        this.$refs.table.refresh();
        this.filtersApplied = false;
        this.resetSelectedDeliveryNotes();
      });
    },
    async stableDeliveryNote(id) {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$t("deliveryNotes.confirm_mark_as_cofirmed"),
        icon: "/assets/icon/check-circle-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async value => {
        if (value) {
          const data = {
            delivery_note_id: id
          };
          let response = await this.markAsStable(data);
          this.refreshTable();
          if (response.data) {
            window.toastr["success"](
              this.$tc("deliveryNotes.mark_as_cofirmed_successfully")
            );
          }
        }
      });
    },
    async convertInToinvoice(id) {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$t("pos.confirm_conversion_invoice"),
        icon: "/assets/icon/file-alt-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willConvertInToinvoice => {
        if (willConvertInToinvoice) {
          let data = {
            delivery_note_id: id
          };
          let res = await this.convertToInvoice(data);
          if (res.data && res.data.result === true) {
            window.toastr["success"](this.$t("pos.conversion_message"));
            this.$router.push(`manually-invoices/${res.data.invoice.id}/edit`);
          } else if (res.data.error) {
            window.toastr["error"](res.data.message);
          }
        }
      });
    },
    async downloadDeliveryNote(uniqueHash) {
      window.open(`/deliveryNote/export/${uniqueHash}`);
    }
  }
};
</script>
